import React from 'react'
import { observer } from 'mobx-react-lite'

import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { P } from '@mxenabled/mxui'

import { EmptyState, useFinstrongStore, useGlobalCopyStore, useUserStore } from '../../../common'
import { type MonthlySummaries } from '../../../core'
import { LineChart } from '../../../common/components/charts/LineChart'
import { useDimensions } from '../../../common/hooks/useDimensions'

import { getSeriesGraphData, getCreditScoreDetails } from '../../util/finstrongUtils'

// Define chart types
export type ChartType = 'savingsOverTime' | 'debtOverTime' | 'creditOverTime'

// Configuration interface for chart properties
interface ChartConfig {
  title: string
  valueKey: keyof Pick<MonthlySummaries, 'available_cash' | 'debt_balance' | 'credit_score'>
}

interface LineGraphProps {
  chartFor: ChartType
}

const LineGraph: React.FC<LineGraphProps> = ({ chartFor }) => {
  const theme = useTheme()
  const isDarkMode = theme.palette.mode === 'dark'

  const { monthlySummaries } = useFinstrongStore()
  const { user } = useUserStore()

  const { finstrong: copy } = useGlobalCopyStore()
  const [containerRef, { width }] = useDimensions()

  const ZeroStateContent = {
    savingsOverTime: {
      icon: 'trending_up',
      title: copy.key_indicators_save.zero_state_title,
      description: copy.key_indicators_save.zero_state_description,
    },
    debtOverTime: {
      icon: 'multiline_chart',
      title:
        copy.key_indicators_borrow.have_manageable_debt_drawer
          .have_manageable_debt_line_chart_empty_state_header,
      description:
        copy.key_indicators_borrow.have_manageable_debt_drawer
          .have_manageable_debt_line_chart_empty_state_description,
    },
    creditOverTime: {
      icon: 'credit_card',
      title: copy.credit_over_time_title,
      description: copy.credit_over_time_zero_state_desc,
    },
  }[chartFor]

  const chartConfigurations: Record<ChartType, ChartConfig> = {
    savingsOverTime: {
      title: copy.key_indicators_save.savings_over_time,
      valueKey: 'available_cash',
    },
    debtOverTime: {
      title: copy.key_indicators_borrow.have_manageable_debt_drawer.debt_over_time,
      valueKey: 'debt_balance',
    },
    creditOverTime: {
      title: copy.credit_over_time_title,
      valueKey: 'credit_score',
    },
  }

  // Get configuration for the current chart type
  const config = chartConfigurations[chartFor]

  const dataSet = getSeriesGraphData(monthlySummaries, config.valueKey)

  const values = monthlySummaries.map((s) => s[config.valueKey])
  const maxValue = Math.max(...values)
  const minValue = Math.min(...values)

  // Determine appropriate formatter and margin based on data range
  const getFormatterConfig = () => {
    if (maxValue >= 1000000) {
      return { formatter: '0.000a', leftMargin: -7 } // For millions
    } else if (maxValue >= 1000) {
      return { formatter: '0a', leftMargin: -27 } // For thousands
    } else {
      return { formatter: '0a', leftMargin: -32 }
    }
  }

  const { formatter, leftMargin } = getFormatterConfig()

  const title = config.title

  const isCreditOverTime = chartFor === 'creditOverTime'

  let markerColor

  if (isCreditOverTime) {
    const { color } = getCreditScoreDetails(
      user?.credit_score ?? 0,
      isDarkMode,
      copy.credit_score_status,
    )
    markerColor = color
  }
  const hasData = dataSet.some((item) => item.y !== 0)
  return (
    <Card ref={containerRef} sx={{ backgroundColor: 'background.paper' }}>
      {hasData ? (
        <Stack gap={8} sx={{ pl: 18 }}>
          <P sx={{ pb: 8, pt: 16 }} variant="h3">
            {title}
          </P>
          <LineChart
            areaColor={markerColor}
            axisColor={theme.palette.divider}
            baseline="min"
            chartFor={chartFor}
            colors={[theme.palette.primary.main]}
            curveType={isCreditOverTime ? 'linear' : 'monotoneX'}
            datasets={dataSet ? [dataSet] : []}
            height={244}
            labelFontColor={isCreditOverTime ? 'text.primary' : ''}
            labelFontSize={isCreditOverTime ? 13 : undefined}
            labels={[copy.key_indicators_label]}
            margin={{ left: leftMargin, top: isCreditOverTime ? 25 : 15, right: 20 }}
            markStyles={(index) => {
              if (isCreditOverTime) {
                return {
                  markLabelStyles: {
                    marginLeft: 16,
                    transform:
                      index === dataSet.length - 1 ? 'translateX(-16px)' : 'translateX(16px)',
                    fontWeight: 600,
                    fill: theme.palette.text.primary,
                  },
                  stroke: markerColor!,
                }
              }
              return {}
            }}
            maxRange={isCreditOverTime ? 850 : undefined}
            minRange={(() => {
              if (!isCreditOverTime) return undefined
              return minValue < 300 ? 0 : 300
            })()}
            showArea={true}
            showAverage={!isCreditOverTime}
            showMark={!isCreditOverTime}
            showMarkLabel={isCreditOverTime}
            showXAxis={true}
            showYAxis={isCreditOverTime}
            showYAxisTicks={isCreditOverTime}
            useCustomMark={true}
            valueFormatterString={!isCreditOverTime ? formatter : undefined}
            width={width < 342 ? width - 48 : 342}
          />
        </Stack>
      ) : (
        <EmptyState
          header={ZeroStateContent.title}
          icon={ZeroStateContent.icon}
          subText={ZeroStateContent.description}
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: '8px',
            boxShadow: 1,
            p: 24,
            pb: 48,
            '.empty-state-subtext': {
              fontSize: 15,
              lineHeight: '24px',
              mt: 8,
            },
          }}
        />
      )}
    </Card>
  )
}

export default observer(LineGraph)
